import React, { useCallback } from 'react';
import { useReactFlow, Node } from 'reactflow';

import { CustomNode, NodeType, AnyNodeData } from '../../types';
import { ENVIRONMENTS, MODULES } from '../../types/cluster';
import { snapPositionToGrid } from '../../utils/grid';
import { generateId } from './utils';
import { getDefaultHandles } from '../left-panel/NodeCreator'; // Import getDefaultHandles
import { getDefaultNodeWidth } from '../../utils/nodeHelpers'; // Import getDefaultNodeWidth

type CanvasMode = 'project' | 'playbook';

const mapDraggedTypeToNodeType = (draggedType: NodeType): AnyNodeData['type'] => {
    // ... (mapping logic remains the same) ...
     switch (draggedType) {
        case 'javascript-source':
        case 'mobile-sdk':
        case 'server-source':
            return 'source';
        case 'destination':
            return 'destination';
        case 'cluster':
            return 'cluster';
        case 'pipeline':
            return 'pipeline';
        case 'sync-injector':
            return 'sync-injector';
        case 'load-balancer':
            return 'load-balancer';
        case 'transformations':
        case 'mapping':
        case 'enrichment':
        case 'lua':
        case 'spreading':
        case 'schema-enforcement':
        case 'bot-filtering':
        case 'data-replay':
            return 'processor';
        case 'filter':
            return 'filter';
        case 'event':
            return 'event';
        case 'text-node':
            return 'text-node';
        case 'notes-node':
            return 'notes-node';
        case 'group':
            return 'group';
        default:
            console.warn(`Unknown dragged type encountered: ${draggedType}. Mapping to 'unknown'.`);
            return 'unknown' as any;
    }
};

// Define the props the hook needs, including actions/state from the store
export interface DropHandlerProps { // Export the type
    isEmbedded: boolean;
    reactFlowWrapperRef: React.RefObject<HTMLDivElement>;
    mode: CanvasMode;
    nodes: Node<AnyNodeData>[]; // Use generic Node type
    addNode?: (node: CustomNode) => void; // Project specific action
    addPlaybookNode?: (node: Node) => void; // Playbook specific action
    deleteNode?: (id: string) => void; // Action needed? (Not used directly in drop)
    // Accept the union of function types provided by the selector
    setSelectedNode?: ((node: CustomNode | null) => void) | ((node: Node | null) => void);
}

interface DropHandlers {
    onDragOver: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => void;
}

export const useDropHandlers = ({
    isEmbedded,
    reactFlowWrapperRef,
    mode,
    nodes: allNodes, // Rename for clarity
    addNode,
    addPlaybookNode,
    setSelectedNode,
}: DropHandlerProps): DropHandlers => {
    // Use screenToFlowPosition instead of project
    const { screenToFlowPosition } = useReactFlow();

    const onDragOver = useCallback((event: React.DragEvent) => {
        // ... (logic remains the same) ...
         event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        if (reactFlowWrapperRef.current && !reactFlowWrapperRef.current.classList.contains('dragging-over')) {
             reactFlowWrapperRef.current.classList.add('dragging-over');
        }
    }, [reactFlowWrapperRef]);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            if (isEmbedded) return;
            event.preventDefault();

            // Actions are now passed directly via props
            const addAction = mode === 'playbook' ? addPlaybookNode : addNode;
            if (!addAction || !setSelectedNode) {
                console.error(`[Canvas] Missing required ${mode} store actions for drop handler.`);
                return;
            }

             if (reactFlowWrapperRef.current) {
                reactFlowWrapperRef.current.classList.remove('dragging-over');
            }
            if (!reactFlowWrapperRef.current) return;

            // reactFlowBounds is no longer needed with screenToFlowPosition

            // --- Read Drag Data ---
            let type: string;
            let initialData: Partial<AnyNodeData> = {};

            try {
                const dataString = event.dataTransfer.getData("application/reactflow");
                if (!dataString) {
                    console.error("[Canvas] Error: No data found in drag event for application/reactflow");
                    return;
                }
                const parsedData = JSON.parse(dataString);

                if (!parsedData.type) {
                     console.error("[Canvas] Error: No 'type' found in dragged data", parsedData);
                     return;
                }
                type = parsedData.type;
                initialData = parsedData.data || {};

                // --- Compatibility Check (Project Mode) ---
                if (mode === 'project') {
                    const legacyNodeDataString = event.dataTransfer.getData("application/node-data");
                    if (legacyNodeDataString) {
                        try {
                            const legacyNodeData = JSON.parse(legacyNodeDataString);
                            initialData = { ...initialData, ...legacyNodeData };
                            console.log("[Canvas] Using legacy node data:", legacyNodeData);
                        } catch (legacyError) {
                             console.error("[Canvas] Error parsing legacy node data:", legacyError);
                        }
                    } else if (!parsedData.data) {
                        console.error("[Canvas] Error: Project mode drop missing node data in both formats.");
                        return;
                    }
                }
                // --- End Compatibility Check ---

            } catch (error) {
                console.error("[Canvas] Error parsing dragged data:", error);
                return;
            }
            // --- End Read Drag Data ---

            if (typeof type === "undefined" || !type) {
                 console.error("[Canvas] Error: Node type is still undefined after parsing.");
                return;
            }

            // Calculate position directly using screenToFlowPosition
            const rawPosition = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            const position = snapPositionToGrid(rawPosition);

            const label = initialData.label || `${type} node`;
            const newNodeId = generateId(`${type}-`);

            let parentGroupId: string | undefined = undefined;
            const groupNodes = allNodes.filter((n: Node) => n.type === 'group');
            for (const groupNode of groupNodes) {
                if (!groupNode.position || !groupNode.width || !groupNode.height) continue;
                const isInside =
                    position.x >= groupNode.position.x &&
                    position.x <= groupNode.position.x + groupNode.width &&
                    position.y >= groupNode.position.y &&
                    position.y <= groupNode.position.y + groupNode.height;
                if (isInside) {
                    parentGroupId = groupNode.id;
                    break;
                }
            }

            // --- Create New Node Object ---
            let newNode: Node<AnyNodeData>;

            const baseNodeProps: Omit<Node<AnyNodeData>, 'data'> = {
                id: newNodeId,
                type: type,
                draggable: true,
                position: parentGroupId
                    ? snapPositionToGrid({
                          x: position.x - (allNodes.find((n: Node) => n.id === parentGroupId)?.position?.x || 0),
                          y: position.y - (allNodes.find((n: Node) => n.id === parentGroupId)?.position?.y || 0)
                      })
                    : position,
                width: getDefaultNodeWidth(type), // Use getDefaultNodeWidth for dynamic width
                height: type === 'group' ? 200 : undefined,
                selected: false,
                dragging: false,
                positionAbsolute: position,
            };

            const parentProps = parentGroupId
               ? { parentId: parentGroupId, extent: 'parent' as const, zIndex: 1000 }
               : {};

            if (mode === 'playbook') {
                newNode = {
                    ...baseNodeProps,
                    ...parentProps,
                    data: {
                        ...initialData,
                        label: label,
                    } as AnyNodeData,
                };
                 console.log("[Canvas] Creating Playbook Node:", newNode);
            } else {
                // Project mode logic...
                if (type === 'group') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'group',
                            name: label,
                            hasIncomingHandle: false,
                            hasOutgoingHandle: false,
                            color: (initialData as Partial<import('../../types').GroupNodeData>).color ?? undefined, // Use color from initialData or default to undefined
                            icon: 'box',
                            groupNameVisibility: 'visible',
                            iconVisibility: 'visible',
                            customLabelVisibility: 'visible',
                            allowedNodeTypes: [],
                            customLabel: '',
                        },
                    };
                } else if (type.includes('source')) {
                     const subType = (() => {
                        if (type === 'javascript-source') return 'javascript';
                        if (type === 'mobile-sdk') return 'mobile-sdk';
                        if (type === 'server-source') return 'server';
                        return 'javascript';
                    })();
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: "source",
                            subType: (initialData as Partial<import('../../types').SourceNodeData>).subType || subType,
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType), // Assign default handles or empty array if shift is held
                        },
                    };
                } else if (type === 'destination') {
                     // Construct data object separately
                     const destinationData: import('../../types').DestinationNodeData = {
                         // Spread initialData first
                         ...(initialData as Partial<import('../../types').DestinationNodeData>),
                         // Ensure required fields are present and correctly typed
                         label: label,
                         type: "destination",
                         handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType), // Assign default handles or empty array if shift is held
                         latencyImpactVisibility: 'hidden', // Explicitly set to hidden here
                         // Ensure other potentially missing required fields from DestinationNodeData have defaults if necessary
                         // (Example: Assuming 'project' and 'name' are required)
                         project: initialData.project ?? '',
                         name: initialData.name ?? label,
                         // Add defaults for other required fields if DestinationNodeData requires them
                     };

                     newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: destinationData, // Assign the constructed data object
                    };
                } else if (type === 'cluster') {
                    const defaultEnv = ENVIRONMENTS.find(env => env.id === 'mr-managed');
                    const defaultModules = MODULES
                        .filter(m => m.isEnabled)
                        .map(m => ({ moduleId: m.id, enabled: false }));
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'cluster',
                            clusterEnvironment: {
                                environmentId: 'mr-managed',
                                name: 'MetaRouter Managed',
                                hintText: defaultEnv?.hintText
                            },
                            modules: defaultModules,
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'pipeline') {
                    const defaultModules = MODULES
                        .filter(m => m.isEnabled)
                        .map(m => ({ moduleId: m.id, enabled: false }));
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'pipeline',
                            name: label,
                            modules: defaultModules,
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'sync-injector') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'sync-injector',
                            status: "active",
                            domains: [],
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'load-balancer') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'load-balancer',
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'filter') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'filter',
                            filterType: "",
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'event') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'event',
                            name: label,
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'transformations') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'processor',
                            nodeType: 'transformations',
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'schema-enforcement') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'processor',
                            nodeType: 'schema-enforcement',
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'bot-filtering') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'processor',
                            nodeType: 'bot-filtering',
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else if (type === 'data-replay') {
                    newNode = {
                        ...baseNodeProps,
                        ...parentProps,
                        data: {
                            label: label,
                            project: initialData.project ?? '',
                            type: 'processor',
                            nodeType: 'data-replay',
                            handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                        },
                    };
                } else {
                    const mappedType = mapDraggedTypeToNodeType(type as NodeType);
                    let finalData: AnyNodeData = {
                       label: label,
                       project: initialData.project ?? '',
                       type: mappedType,
                       ...(initialData as any),
                       handles: event.shiftKey ? [] : getDefaultHandles(type as NodeType),
                    };
                    newNode = {
                       ...baseNodeProps,
                       ...parentProps,
                       data: finalData,
                    };
                }
            }
            // --- End Create New Node Object ---

            // --- Add Node to Store ---
            addAction(newNode as any); // Use the correct add action

            // Select the newly added node
            setSelectedNode(newNode as any);

        },
        // Update dependencies: replace projectPosition with screenToFlowPosition and remove reactFlowWrapperRef if only used for bounds
        [isEmbedded, reactFlowWrapperRef, screenToFlowPosition, mode, allNodes, addNode, addPlaybookNode, setSelectedNode]
    );

    return { onDragOver, onDrop };
};
